<template>
  <el-popover
    v-if="!loadingInitial && devices.length > 0"
    v-model="popoverOpen"
    placement="bottom-end"
    trigger="click"
    popper-class="stp__popper"
    :width="320"
    @show="onPopoverShow"
  >
    <div class="stp">
      <p class="stp__title">Send to a POS device</p>
      <p v-if="loading" class="stp__hint">Loading devices…</p>
      <p v-else-if="loadError" class="stp__error">{{ loadError }}</p>
      <ul v-else-if="devices.length > 0" class="stp__list">
        <li
          v-for="d in devices"
          :key="d.device_type + '-' + d.id"
          class="stp__row"
          :class="{
            'stp__row--offline': !d.is_online,
            'stp__row--sending': sendingDeviceId === d.id
          }"
          role="button"
          tabindex="0"
          @click="onPick(d)"
          @keydown.enter="onPick(d)"
        >
          <span class="stp__dot" :class="d.is_online ? 'stp__dot--on' : 'stp__dot--off'"></span>
          <div class="stp__row-body">
            <p class="stp__row-name">{{ d.name }}</p>
            <p class="stp__row-meta">
              {{ d.payment_page_name || (isReader(d) ? 'Stripe reader' : '') }}
              <span v-if="!d.is_online" class="stp__row-status">· offline</span>
            </p>
          </div>
          <i v-if="sendingDeviceId === d.id" class="el-icon-loading stp__row-spinner"></i>
          <i v-else class="tim-icons icon-double-right stp__row-arrow"></i>
        </li>
      </ul>
      <p v-else class="stp__hint">No POS devices on this group yet.</p>
    </div>

    <button
      v-if="variant === 'header-cta' || variant === 'field'"
      slot="reference"
      type="button"
      class="stp__header-cta"
      :class="{ 'stp__header-cta--field': variant === 'field' }"
      :disabled="disabled || sendingDeviceId !== null"
    >
      <i class="tim-icons icon-tablet-2 mr-1"></i>
      Send to POS
    </button>

    <base-button
      v-else
      slot="reference"
      :type="buttonType"
      :size="buttonSize"
      :simple="simple"
      :disabled="disabled || sendingDeviceId !== null"
    >
      <i class="tim-icons icon-tablet-2 mr-1"></i>
      Send to POS
    </base-button>
  </el-popover>
</template>

<script>
import axios from 'axios';
import { Auth } from 'aws-amplify';
import { API_LOCATION } from '@/api-config';
import posPaymentService from '@/services/posPayment';
import { canSendToPos } from '@/util/posBeta';

export default {
  name: 'SendToPosButton',

  props: {
    // Required unless pickOnly: the transaction to dispatch on pick.
    transactionId: { type: Number, default: null },
    // pickOnly: choosing a device only emits `pick` with the device row; the
    // caller dispatches later (OTC-3111 gift card purchase sends on Create).
    pickOnly: { type: Boolean, default: false },
    // Amount due in dollars — used purely for the in-progress drawer's
    // display ("Awaiting $X.XX"). Optional; if omitted the drawer hides
    // the amount line.
    amount: { type: Number, default: null },
    buttonType: { type: String, default: 'primary' },
    buttonSize: { type: String, default: 'sm' },
    simple: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    /**
     * Visual style of the trigger button.
     * - 'default'    — base-button (primary or simple pink). Used in TransactionDetails.
     * - 'header-cta' — matches the drawer-header-cta-secondary look used by
     *                  BookingDrawer's header action row.
     * - 'field'      — header-cta tint at form control height (38px, same as
     *                  a drawer select), for a button that sits in a form row
     *                  beside a field.
     */
    variant: {
      type: String,
      default: 'default',
      validator: (v) => ['default', 'header-cta', 'field'].includes(v),
    },
  },

  data() {
    return {
      popoverOpen: false,
      loadingInitial: true,
      loading: false,
      loadError: '',
      devices: [],
      sendingDeviceId: null,
    };
  },

  mounted() {
    // Users who can't send orders (payments_update) never fetch devices
    // or render the button — sending requires the same permission
    // server-side. See src/util/posBeta.js.
    if (!canSendToPos()) {
      this.loadingInitial = false;
      return;
    }
    this.fetchDevices();
  },

  methods: {
    async getAuthToken() {
      const data = await Auth.currentSession();
      return data.idToken.jwtToken;
    },

    ctx() {
      const companyId = localStorage.getItem('companyId');
      const groupRaw = localStorage.getItem('group');
      const companyGroupId = groupRaw ? JSON.parse(groupRaw).id : null;
      return { companyId, companyGroupId };
    },

    async fetchDevices() {
      const { companyId, companyGroupId } = this.ctx();
      if (!companyId || !companyGroupId) {
        this.loadError = 'Missing company context';
        this.loading = false;
        this.loadingInitial = false;
        return;
      }
      this.loading = true;
      this.loadError = '';
      try {
        const token = await this.getAuthToken();
        const url = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/pos-devices`;
        const res = await axios.get(url, { headers: { Authorization: token } });
        this.devices = res.data?.devices || [];
      } catch (err) {
        console.error('Failed to load POS devices', err);
        const body = err.response?.data;
        const parsed = typeof body === 'string' ? body : body?.error;
        this.loadError = parsed || 'Could not load POS tablets';
      } finally {
        this.loading = false;
        this.loadingInitial = false;
      }
    },

    onPopoverShow() {
      // Refresh on each open — online status changes quickly as tablets
      // sleep/wake and a stale dot misleads staff.
      this.fetchDevices();
    },

    isReader(d) {
      return d.device_type === 'stripe_terminal_reader';
    },
    async onPick(device) {
      if (this.sendingDeviceId !== null) return;

      // Strict single-session policy: refuse a new dispatch while one
      // is in-flight. Operator must cancel (or wait for completion)
      // before sending another. Prevents orphaned sessions across
      // devices and a customer being prompted on two surfaces at once.
      // Terminal states (paid/cancelled/expired) don't count — those
      // are in their post-flow dismiss countdown.
      const existing = posPaymentService.state.activeSession;
      if (existing && !['paid', 'failed', 'cancelled', 'expired'].includes(existing.status)) {
        this.$notify({
          type: 'warning',
          message: `A payment is already in progress on ${existing.deviceName}. Cancel it before starting another.`,
        });
        // Bring the active drawer to the operator's attention.
        posPaymentService.expand();
        this.popoverOpen = false;
        return;
      }

      if (!device.is_online) {
        const offlineMsg = this.isReader(device)
          ? `${device.name} is offline. Check it's powered on and connected to Wi-Fi.`
          : `${device.name} is offline. Wake the tablet and try again.`;
        this.$notify({ type: 'warning', message: offlineMsg });
        return;
      }
      if (this.pickOnly) {
        this.popoverOpen = false;
        this.$emit('pick', device);
        return;
      }
      this.sendingDeviceId = device.id;
      const transactionId = this.transactionId;
      try {
        // One dispatch path for every surface: the service posts send-to-pos
        // and hands off to the global in-progress tracker (DashboardLayout
        // level, so it persists across navigation while the customer taps).
        const data = await posPaymentService.sendToDevice({
          transactionId,
          device,
          amount: this.amount,
        });
        const successMsg = this.isReader(device)
          ? `Dispatched to ${device.name}. Customer can tap card when ready.`
          : `Sent to ${device.name}. Customer can tap when ready.`;
        this.$notify({ type: 'success', message: successMsg });
        this.popoverOpen = false;

        this.$emit('sent', {
          transaction_id: transactionId,
          device_id: device.id,
          device_type: device.device_type,
          device_name: device.name,
          session_hash: data.session_hash,
          expires_at: data.expires_at,
          payment_intent_id: data.payment_intent_id || null,
        });
      } catch (err) {
        console.error('Send to POS failed', err);
        const body = err.response?.data;
        const parsed = typeof body === 'string' ? body : body?.error;
        const status = err.response?.status;
        let message = parsed || 'Could not send to POS';
        // Reader outcomes carry one of two flags: already paid (recorded now,
        // the parent refreshes) or the pending order was cancelled because the
        // dispatch could not go ahead. Any other 409 (device revoked, archived
        // page) keeps the generic handling below so staff can pick another device.
        const alreadyPaid = !!(body && typeof body === 'object' && body.already_paid === true);
        const orderCancelled = !!(body && typeof body === 'object' && body.pending_order_cancelled === true);
        if (status === 409 && (alreadyPaid || orderCancelled)) {
          this.$notify({
            type: 'warning',
            // The parent refreshes or closes on already-paid, so the server's
            // "refresh the order" instruction is replaced here.
            message: alreadyPaid
              ? 'This order was already paid on the reader. The payment has been recorded.'
              : (orderCancelled ? `${message} The order was cancelled; create it again.` : message),
          });
          if (alreadyPaid) {
            this.$emit('already-paid', { transaction_id: transactionId });
          }
          if (orderCancelled) {
            this.$emit('order-cancelled', { transaction_id: transactionId });
          }
          this.popoverOpen = false;
          return;
        }
        if (status === 503) {
          message = this.isReader(device)
            ? `${device.name} went offline before dispatch. Try again or pick another device.`
            : `${device.name} dropped offline before delivery. Try again or pick another tablet.`;
          const d = this.devices.find((x) => x.id === device.id);
          if (d) d.is_online = false;
        }
        this.$notify({ type: 'danger', message });
      } finally {
        this.sendingDeviceId = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Header-CTA trigger — mirrors BookingDrawer's .drawer-header-cta-secondary
// so the button visually sits with "Collect $X" et al. in the drawer header.
.stp__header-cta {
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  border: 1px solid rgba(225, 78, 202, 0.4);
  border-radius: 6px;
  background: rgba(225, 78, 202, 0.1);
  color: #e14eca;
  white-space: nowrap;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, box-shadow 0.15s ease;

  &:hover:not(:disabled) {
    background: rgba(225, 78, 202, 0.2);
    box-shadow: 0 0 0 3px rgba(225, 78, 202, 0.15);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  i {
    font-size: 12px;
  }

  &--field {
    height: 38px;
    padding: 0 14px;
    flex: 0 0 auto;
    border-radius: 0.4285rem;
  }
}

.stp {
  padding: 4px 0;

  &__title {
    margin: 0 0 8px;
    font-size: 13px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
  }

  &__hint {
    margin: 4px 0 0;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.55);
  }

  &__error {
    margin: 4px 0 0;
    font-size: 12px;
    color: #fd5d93;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 8px;
    margin: 0 -8px;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.12s ease;
    outline: none;

    &:hover,
    &:focus-visible {
      background: rgba(225, 78, 202, 0.1);
    }

    &--offline {
      cursor: not-allowed;
      opacity: 0.55;

      &:hover,
      &:focus-visible {
        background: rgba(255, 255, 255, 0.04);
      }
    }

    &--sending {
      cursor: progress;
      pointer-events: none;
    }
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;

    &--on {
      background: #00f2c3;
      box-shadow: 0 0 0 3px rgba(0, 242, 195, 0.18);
    }

    &--off {
      background: rgba(255, 255, 255, 0.2);
    }
  }

  &__row-body {
    flex: 1;
    min-width: 0;
  }

  &__row-name {
    margin: 0;
    font-size: 13px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.95);
  }

  &__row-meta {
    margin: 2px 0 0;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.55);
  }

  &__row-status {
    color: rgba(255, 255, 255, 0.45);
  }

  &__row-arrow {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.4);
    flex-shrink: 0;
  }

  &__row-spinner {
    color: #e14eca;
    flex-shrink: 0;
  }
}

// Light-mode parity
.white-content {
  .stp__title { color: rgba(0, 0, 0, 0.9); }
  .stp__hint { color: rgba(0, 0, 0, 0.55); }
  .stp__row-name { color: rgba(0, 0, 0, 0.9); }
  .stp__row-meta { color: rgba(0, 0, 0, 0.55); }
  .stp__row-status { color: rgba(0, 0, 0, 0.45); }
  .stp__row-arrow { color: rgba(0, 0, 0, 0.4); }
  .stp__dot--off { background: rgba(0, 0, 0, 0.2); }

  .stp__row:hover,
  .stp__row:focus-visible {
    background: rgba(225, 78, 202, 0.08);
  }
  .stp__row--offline:hover,
  .stp__row--offline:focus-visible {
    background: rgba(0, 0, 0, 0.04);
  }
}
</style>

<!--
  Element UI teleports el-popover content to document.body, so scoped
  styles can't reach it. This unscoped block themes the popper itself —
  background, border, arrow — matching the rest of OTC's dark surface.
  Targeted via the popper-class="stp__popper" attribute on el-popover.
-->
<style lang="scss">
.stp__popper.el-popover {
  background: #171826;
  border: 1px solid rgba(225, 78, 202, 0.3);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  color: rgba(255, 255, 255, 0.95);
}

// Arrow color must match popper bg / border so the tip blends.
// Element UI uses two stacked arrows — outer (border) + ::after (fill).
.stp__popper.el-popover[x-placement^="bottom"] .popper__arrow {
  border-bottom-color: rgba(225, 78, 202, 0.3);
}
.stp__popper.el-popover[x-placement^="bottom"] .popper__arrow::after {
  border-bottom-color: #171826;
}
.stp__popper.el-popover[x-placement^="top"] .popper__arrow {
  border-top-color: rgba(225, 78, 202, 0.3);
}
.stp__popper.el-popover[x-placement^="top"] .popper__arrow::after {
  border-top-color: #171826;
}

// Light-mode parity. body.white-content wraps the entire document including
// teleported poppers, so the descendant selector reaches them.
.white-content .stp__popper.el-popover {
  background: #ffffff;
  border-color: rgba(225, 78, 202, 0.3);
  color: rgba(0, 0, 0, 0.9);
}
.white-content .stp__popper.el-popover[x-placement^="bottom"] .popper__arrow::after {
  border-bottom-color: #ffffff;
}
.white-content .stp__popper.el-popover[x-placement^="top"] .popper__arrow::after {
  border-top-color: #ffffff;
}
</style>
